import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Modal,
  Tabs,
  Tab,
  Menu,
  MenuItem,
  Tooltip,
  Divider,
  Grid,
  Card,
  CardContent,
  Chip,
  InputBase,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  History as HistoryIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  MoreVert as MoreVertIcon,
  ContentCopy as ContentCopyIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  AccessTime as AccessTimeIcon,
  CalendarToday as CalendarTodayIcon
} from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEnvelope,
  faComment,
  faFile,
  faChartBar,
  faCalendar,
  faBookmark,
  faStickyNote,
  faAddressCard,
  faBuilding,
  faCircle,
  faClock,
  faClone,
  faCompass,
  faCopyright,
  faCreditCard,
  faFaceSmile,
  faFileAudio,
  faFileCode,
  faFileExcel,
  faFileImage,
  faFilePdf,
  faFilePowerpoint,
  faFileVideo,
  faFileWord,
  faFileZipper,
  faFolderOpen,
  faHdd,
  faHeart,
  faHourglass,
  faImage,
  faKeyboard,
  faLightbulb,
  faMap,
  faMoon,
  faNewspaper,
  faPaperPlane,
  faPaste,
  faPenToSquare,
  faRectangleList,
  faStar,
  faSun,
  faThumbsUp,
  faUser
} from '@fortawesome/free-regular-svg-icons';
import {
  faTag,
  faGlobe,
  faAnchor,
  faArrowUpRightDots,
  faBell,
  faBolt,
  faBrain,
  faBriefcase,
  faBullhorn,
  faCamera,
  faCartShopping,
  faChartPie,
  faChess,
  faCode,
  faCoffee,
  faCogs,
  faCoins,
  faDatabase,
  faDiagramProject,
  faEarthAmericas,
  faFire,
  faFlag,
  faFlask,
  faGaugeHigh,
  faGears,
  faGem,
  faGraduationCap,
  faHeadset,
  faHouse,
  faInfo,
  faLink,
  faMagnifyingGlass,
  faMapLocation,
  faMicrochip,
  faMicrophone,
  faMusic,
  faPalette,
  faPenRuler,
  faPeopleGroup,
  faPhone,
  faPrint,
  faPuzzlePiece,
  faRobot,
  faRocket,
  faScroll,
  faShield,
  faShoppingBag,
  faSignal,
  faSitemap,
  faStopwatch,
  faTableList,
  faTerminal,
  faToolbox,
  faTrophy,
  faUserGear,
  faVideo,
  faWifi
} from '@fortawesome/free-solid-svg-icons';
import SlackIcon from '../icons/SlackIcon';
import SalesforceIcon from '../icons/SalesforceIcon';
import { useAuth } from '@clerk/clerk-react';
import { formatDistanceToNow } from 'date-fns';
import { API_URL } from '../../config';
import JsPDF from 'jspdf';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store/store';
import PromptTab from '../prompt-tab/PromptTab';
import logo from "../../images/tranzcribe-logo-text-black.png";
import { isMobile } from 'react-device-detect';
import { showErrorToast, showSuccessToast, showWarningToast } from '../../toastUtils';
import { fetchGlobalPrompts, SubPrompt } from "../../store/promptsSlice";
import { AppDispatch } from "../../store/store";
import { useAppSettings } from '../../contexts/AppSettingsContext';
import Footer from '../common/Footer';

// Define interfaces
interface Transcription {
  id: string;
  title: string;
  createdAt: string;
  duration: number;
  textContent: string;
}

interface PaginationData {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const HistoryPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [transcriptions, setTranscriptions] = useState<Transcription[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [pagination, setPagination] = useState<PaginationData>({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0
  });
  const [selectedTranscription, setSelectedTranscription] = useState<Transcription | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0); // Initialize with 0 instead of -1
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [activeTranscriptionId, setActiveTranscriptionId] = useState<string | null>(null);
  const [shareToSlackLoading, setShareToSlackLoading] = useState(false);
  const [shareToSalesforceLoading, setShareToSalesforceLoading] = useState(false);
  const [salesforceError, setSalesforceError] = useState<string | null>(null);
  const [isEditingSalesforceText, setIsEditingSalesforceText] = useState(false);
  const [editedSalesforceText, setEditedSalesforceText] = useState<string>("");
  const [salesforceTextSaved, setSalesforceTextSaved] = useState(false);
  const [selectedPrimaryButton, setSelectedPrimaryButton] = useState<string | null>(null);
  const [selectedSubprompt, setSelectedSubprompt] = useState<SubPrompt | null>(null);
  const [processedPromptText, setProcessedPromptText] = useState<string | null>(null);
  const [processingSubprompt, setProcessingSubprompt] = useState(false);
  const [loadingPromptData, setLoadingPromptData] = useState(false);
  const [promptDisplayText, setPromptDisplayText] = useState<string | null>(null);
  const [isEditingTranscription, setIsEditingTranscription] = useState(false);
  const [editedTranscription, setEditedTranscription] = useState<string>("");
  const [updatingTranscription, setUpdatingTranscription] = useState(false);
  const [moreMenuAnchorEl, setMoreMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [editingPromptIndex, setEditingPromptIndex] = useState<number | null>(null);
  const [editedPromptTexts, setEditedPromptTexts] = useState<{ [key: number]: string }>({});
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [blockedFeature, setBlockedFeature] = useState<'Slack Integration' | 'Salesforce Integration' | null>(null);
  const [contactFormOpen, setContactFormOpen] = useState(false);
  const [contactFormData, setContactFormData] = useState({
    name: '',
    email: '',
    message: '',
    feature: ''
  });
  const [sendingContactForm, setSendingContactForm] = useState(false);
  const { getToken } = useAuth();

  // Check feature flags from environment variables
  const isSlackEnabled = process.env.REACT_APP_ENABLE_SLACK === 'true';
  const isSalesforceEnabled = process.env.REACT_APP_ENABLE_SALESFORCE === 'true';

  // Check if the current domain is carehub.tranzcribe.com
  const isCareHubDomain = window.location.hostname === 'carehub.tranzcribe.com';

  const dispatch = useDispatch<AppDispatch>();
  const globalPrompts = useSelector((state: RootState) => state.globalPrompts.prompts);
  const globalPromptsLoading = useSelector((state: RootState) => state.globalPrompts.loading);
  const user = useSelector((state: RootState) => state.user.user);

  const { settings, getLogoUrl } = useAppSettings();

  const theme = useTheme();

  // Memoized breakpoint values
  const isMobileView = useMediaQuery(theme.breakpoints.down('sm'));

  // Fetch global prompts on component mount
  useEffect(() => {
    dispatch(fetchGlobalPrompts());
  }, [dispatch]);

  // Format duration to readable time
  const formatDuration = (seconds: number) => {
    if (seconds === 0) {
      return "Text Input";
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Handle opening prompt menu
  const handlePromptMenuOpen = (event: React.MouseEvent<HTMLElement>, transcriptionId: string) => {
    setAnchorEl(event.currentTarget);
    setActiveTranscriptionId(transcriptionId);
  };

  // Handle closing prompt menu
  const handlePromptMenuClose = () => {
    setAnchorEl(null);
    setActiveTranscriptionId(null);
  };

  // Handle selecting a prompt from the menu
  const handlePromptSelect = (promptIndex: number) => {
    const transcription = transcriptions.find(t => t.id === activeTranscriptionId);
    if (transcription) {
      setSelectedTranscription(transcription);
      // Add 1 to the index since the first tab (index 0) is the original transcription
      setActiveTab(promptIndex + 1);
      setOpenModal(true);
    }
    handlePromptMenuClose();
  };

  // New handlers for direct menu actions
  const handleSlackShare = (transcriptionId: string) => {
    if (isCareHubDomain) return; // Don't allow Slack sharing on carehub domain

    const transcription = transcriptions.find(t => t.id === transcriptionId);
    if (transcription) {
      setSelectedTranscription(transcription);
      // Set activeTab to Slack sharing tab index
      const slackTabIndex = globalPrompts.filter(prompt => prompt.enabled).length + 1;
      setActiveTab(slackTabIndex);
      setOpenModal(true);
    }
    handlePromptMenuClose();
  };

  const handleSalesforceShare = (transcriptionId: string) => {
    if (isCareHubDomain) return; // Don't allow Salesforce sharing on carehub domain

    const transcription = transcriptions.find(t => t.id === transcriptionId);
    if (transcription) {
      setSelectedTranscription(transcription);
      // Set activeTab to Salesforce sharing tab index
      const salesforceTabIndex = globalPrompts.filter(prompt => prompt.enabled).length + 2;
      setActiveTab(salesforceTabIndex);
      setOpenModal(true);
    }
    handlePromptMenuClose();
  };

  // Debounce search input to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500); // 500ms debounce time

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset to page 1 when search query changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  // Fetch transcriptions data from API
  const fetchTranscriptions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = await getToken();
      const userId = localStorage.getItem('clerk-user-id');

      if (!token || !userId) {
        throw new Error('Authentication required');
      }

      const response = await fetch(
        `${API_URL}/transcribe/transcriptions?page=${page}&limit=${rowsPerPage}&search=${encodeURIComponent(debouncedSearch)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'x-clerk-user-id': userId
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch transcriptions');
      }

      const data = await response.json();
      setTranscriptions(data.transcriptions);
      setPagination(data.pagination);

    } catch (error) {
      console.error('Error fetching transcriptions:', error);
      setError('Failed to fetch your transcription history. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [getToken, page, rowsPerPage, debouncedSearch]);

  useEffect(() => {
    fetchTranscriptions();
  }, [fetchTranscriptions]);

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  // Handle downloading a transcription as PDF
  const handleDownload = async (text: string | null, filename: string) => {
    if (!text) return;

    // Create a new PDF document
    const doc = new JsPDF();
    doc.setFontSize(12);

    // Add the logo at the top (centered)
    const imgWidth = 75;
    let imgHeight = 35; // Default height, will be updated based on aspect ratio
    const pageWidth = doc.internal.pageSize.getWidth();
    const marginX = (pageWidth - imgWidth) / 2;

    // Get the customized logo URL
    const customLogoUrl = getLogoUrl();

    try {
      // Create a temporary canvas to convert the image to DataURL
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Load the image
      const imageLoadPromise = new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'Anonymous';  // This is important for CORS

        img.onload = () => {
          // Set canvas dimensions to match the image
          canvas.width = img.width;
          canvas.height = img.height;

          // Calculate the height to maintain aspect ratio
          const calculatedImgHeight = imgWidth * (img.height / img.width);

          // Draw the image on canvas
          ctx?.drawImage(img, 0, 0);

          try {
            // Convert to DataURL and add to PDF
            const dataUrl = canvas.toDataURL('image/png');
            doc.addImage(dataUrl, 'PNG', marginX, 10, imgWidth, calculatedImgHeight);

            // Update imgHeight for text positioning
            imgHeight = calculatedImgHeight;

            resolve(true);
          } catch (error) {
            console.error('Error converting image to DataURL:', error);
            reject(error);
          }
        };

        img.onerror = (error) => {
          console.error('Error loading logo image:', error);
          reject(error);
        };

        // Set the src to start loading the image
        img.src = customLogoUrl;
      });

      // Wait for the image to load and be added to the PDF
      await imageLoadPromise;
    } catch (error) {
      console.error('Error processing logo for PDF:', error);
      // Fallback to default logo if there's an error
      imgHeight = 35; // Reset to default height if there's an error
      doc.addImage(logo, 'PNG', marginX, 10, imgWidth, imgHeight);
    }

    // Add the text below the logo
    const lines = doc.splitTextToSize(text, 180);
    doc.text(lines, 10, imgHeight + 20);

    // Add black outline around the PDF page
    const pageHeight = doc.internal.pageSize.getHeight();
    doc.setLineWidth(0.2);
    doc.setDrawColor(0, 0, 0);
    doc.rect(5, 5, pageWidth - 10, pageHeight - 10);

    // Save the PDF
    doc.save(filename.endsWith('.pdf') ? filename : `${filename}.pdf`);
  };

  // View transcription details
  const handleViewTranscription = (transcription: Transcription) => {
    setSelectedTranscription(transcription);
    setActiveTab(0); // Set default tab to 0 instead of -1
    setOpenModal(true);
  };

  // Handle tab change in the modal
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    // Check if user is trying to access Slack or Salesforce tab when disabled
    const slackTabIndex = globalPrompts.filter(prompt => prompt.enabled).length + 1;
    const salesforceTabIndex = globalPrompts.filter(prompt => prompt.enabled).length + 2;

    if (!isCareHubDomain) {
      if (newValue === slackTabIndex && !isSlackEnabled) {
        setBlockedFeature('Slack Integration');
        setUpgradeModalOpen(true);
        return; // Don't change tab
      }

      if (newValue === salesforceTabIndex && !isSalesforceEnabled) {
        setBlockedFeature('Salesforce Integration');
        setUpgradeModalOpen(true);
        return; // Don't change tab
      }
    }

    setActiveTab(newValue);
    // Reset subprompt selection when changing tabs
    setSelectedSubprompt(null);
    setProcessedPromptText(null);
  };

  // Handle delete confirmation dialog
  const openDeleteDialog = (id: string) => {
    setDeletingId(id);
    setDeleteDialogOpen(true);
  };

  // Delete a transcription
  const handleDeleteTranscription = async () => {
    if (!deletingId) return;

    try {
      const token = await getToken();
      const userId = localStorage.getItem('clerk-user-id');

      if (!token || !userId) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`${API_URL}/transcribe/transcriptions/${deletingId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-clerk-user-id': userId
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete transcription');
      }

      // Remove from state
      setTranscriptions(prevTranscriptions =>
        prevTranscriptions.filter(t => t.id !== deletingId)
      );

      // Close dialog
      setDeleteDialogOpen(false);
      setDeletingId(null);

      // Refresh the list if needed
      if (transcriptions.length === 1 && page > 1) {
        setPage(page - 1);
      } else {
        fetchTranscriptions();
      }

    } catch (error) {
      console.error('Error deleting transcription:', error);
      showErrorToast('Failed to delete transcription. Please try again.');
    }
  };

  // Close the modal
  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedTranscription(null);
    setActiveTab(0); // Set to 0 instead of -1
    // Reset subprompt selection when closing the modal
    setSelectedSubprompt(null);
    setProcessedPromptText(null);
  };

  // Handle sharing to Slack
  const handleShareToSlack = async () => {
    if (!selectedTranscription) {
      showWarningToast("No transcription selected to share.");
      return;
    }

    // Check if feature is enabled
    if (!isSlackEnabled) {
      setUpgradeModalOpen(true);
      return;
    }

    try {
      setShareToSlackLoading(true);

      const token = await getToken();
      const userId = localStorage.getItem('clerk-user-id');

      if (!token || !userId) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`${API_URL}/slack/share`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-clerk-user-id': userId
        },
        body: JSON.stringify({ text: selectedTranscription.textContent })
      });

      if (!response.ok) {
        throw new Error('Failed to share to Slack');
      }

      showSuccessToast("Transcription successfully shared to Slack!");
    } catch (error) {
      console.error('Error sharing to Slack:', error);
      showErrorToast(`Failed to share to Slack: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setShareToSlackLoading(false);
    }
  };

  // When transcription changes, reset the edited text and editing state
  useEffect(() => {
    if (selectedTranscription) {
      setEditedSalesforceText(selectedTranscription.textContent);
      setIsEditingSalesforceText(false);
      setSalesforceTextSaved(false);
      setSalesforceError(null);
    }
  }, [selectedTranscription]);

  // Reset Salesforce error when tab changes
  useEffect(() => {
    setSalesforceError(null);
  }, [activeTab]);

  // Reset processed text and selected subprompt when tab or transcription changes
  useEffect(() => {
    setProcessedPromptText(null);
    setSelectedSubprompt(null);
  }, [activeTab, selectedTranscription]);

  // Handle saving edited transcript for Salesforce
  const handleSaveEditedTranscript = async () => {
    if (!selectedTranscription || !editedSalesforceText) {
      setSalesforceError("No content to save");
      return;
    }

    try {
      const token = await getToken();
      const userId = localStorage.getItem('clerk-user-id');

      if (!token || !userId) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`${API_URL}/slack/save-edited-transcript`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-clerk-user-id': userId
        },
        body: JSON.stringify({
          transcriptionId: selectedTranscription.id,
          editedContent: editedSalesforceText,
          purpose: 'salesforce'
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to save edited transcript');
      }

      setSalesforceTextSaved(true);
      setIsEditingSalesforceText(false);
      showSuccessToast("Edited transcript saved successfully!");
    } catch (error: any) {
      console.error('Error saving edited transcript:', error);
      setSalesforceError(error.message || 'Failed to save edited transcript');
    }
  };

  // Check if an edited version exists
  const fetchEditedTranscript = async (transcriptionId: string) => {
    try {
      const token = await getToken();
      const userId = localStorage.getItem('clerk-user-id');

      if (!token || !userId) {
        return;
      }

      const response = await fetch(`${API_URL}/slack/edited-transcript/${transcriptionId}?purpose=salesforce`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-clerk-user-id': userId
        }
      });

      if (response.status === 404) {
        // No edited transcript found, this is expected and not an error
        return;
      }

      if (!response.ok) {
        return;
      }

      const data = await response.json();
      if (data.editedTranscript) {
        setEditedSalesforceText(data.editedTranscript.editedContent);
        setSalesforceTextSaved(true);
      }
    } catch (error) {
      console.error('Error fetching edited transcript:', error);
    }
  };

  // When viewing a transcription, check if there's an edited version for Salesforce
  useEffect(() => {
    if (selectedTranscription && activeTab === globalPrompts.filter(prompt => prompt.enabled).length + 2) {
      fetchEditedTranscript(selectedTranscription.id);
    }
  }, [selectedTranscription, activeTab]);

  // Handle sharing to Salesforce
  const handleShareToSalesforce = async () => {
    if (!selectedTranscription) {
      setSalesforceError("No transcription available to share.");
      return;
    }

    // Check if feature is enabled
    if (!isSalesforceEnabled) {
      setUpgradeModalOpen(true);
      return;
    }

    try {
      setShareToSalesforceLoading(true);
      setSalesforceError(null);

      const token = await getToken();
      const userId = localStorage.getItem('clerk-user-id');

      if (!token || !userId) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`${API_URL}/slack/share-salesforce`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-clerk-user-id': userId
        },
        body: JSON.stringify({
          transcriptionId: selectedTranscription.id,
          text: selectedTranscription.textContent,
          userEmail: user?.email || 'anonymous@example.com'
        })
      });

      const responseData = await response.json();

      if (!response.ok) {
        // Don't show a toast, but set the error state
        if (response.status === 409 && responseData?.errorCode === 'DUPLICATE_LEAD') {
          setSalesforceError('This information already exists in Salesforce. Duplicate lead detected.');
          return;
        }

        if (responseData?.message) {
          setSalesforceError(responseData.message);
          return;
        }

        setSalesforceError('Failed to share to Salesforce');
        return;
      }

      showSuccessToast("Transcription successfully shared to Salesforce!");
    } catch (error: any) {
      console.error('Error sharing to Salesforce:', error);
      setSalesforceError(error.message || 'Failed to share to Salesforce. Please try again later.');
    } finally {
      setShareToSalesforceLoading(false);
    }
  };

  // Get icon for prompt (table display)
  const getPromptIcon = (iconType: string | undefined) => {
    // Default to 'email' if iconType is undefined or null
    const icon = iconType || 'email';

    switch (icon) {
      // Communication
      case 'email':
        return <FontAwesomeIcon icon={faEnvelope} size="lg" />;
      case 'message':
        return <FontAwesomeIcon icon={faComment} size="lg" />;
      case 'paper-plane':
        return <FontAwesomeIcon icon={faPaperPlane} size="lg" />;
      case 'phone':
        return <FontAwesomeIcon icon={faPhone} size="lg" />;
      case 'bullhorn':
        return <FontAwesomeIcon icon={faBullhorn} size="lg" />;
      case 'headset':
        return <FontAwesomeIcon icon={faHeadset} size="lg" />;
      case 'microphone':
        return <FontAwesomeIcon icon={faMicrophone} size="lg" />;
      case 'video':
        return <FontAwesomeIcon icon={faVideo} size="lg" />;

      // Documents
      case 'document':
        return <FontAwesomeIcon icon={faFile} size="lg" />;
      case 'file-pdf':
        return <FontAwesomeIcon icon={faFilePdf} size="lg" />;
      case 'file-word':
        return <FontAwesomeIcon icon={faFileWord} size="lg" />;
      case 'file-excel':
        return <FontAwesomeIcon icon={faFileExcel} size="lg" />;
      case 'file-powerpoint':
        return <FontAwesomeIcon icon={faFilePowerpoint} size="lg" />;
      case 'file-image':
        return <FontAwesomeIcon icon={faFileImage} size="lg" />;
      case 'file-audio':
        return <FontAwesomeIcon icon={faFileAudio} size="lg" />;
      case 'file-video':
        return <FontAwesomeIcon icon={faFileVideo} size="lg" />;
      case 'file-code':
        return <FontAwesomeIcon icon={faFileCode} size="lg" />;
      case 'file-zipper':
        return <FontAwesomeIcon icon={faFileZipper} size="lg" />;
      case 'newspaper':
        return <FontAwesomeIcon icon={faNewspaper} size="lg" />;
      case 'folder':
        return <FontAwesomeIcon icon={faFolderOpen} size="lg" />;
      case 'clipboard':
        return <FontAwesomeIcon icon={faPaste} size="lg" />;
      case 'clone':
        return <FontAwesomeIcon icon={faClone} size="lg" />;

      // Analytics & Data
      case 'chart':
        return <FontAwesomeIcon icon={faChartBar} size="lg" />;
      case 'chart-pie':
        return <FontAwesomeIcon icon={faChartPie} size="lg" />;
      case 'signal':
        return <FontAwesomeIcon icon={faSignal} size="lg" />;
      case 'gauge':
        return <FontAwesomeIcon icon={faGaugeHigh} size="lg" />;
      case 'database':
        return <FontAwesomeIcon icon={faDatabase} size="lg" />;
      case 'table':
        return <FontAwesomeIcon icon={faTableList} size="lg" />;
      case 'sitemap':
        return <FontAwesomeIcon icon={faSitemap} size="lg" />;
      case 'diagram':
        return <FontAwesomeIcon icon={faDiagramProject} size="lg" />;
      case 'growth':
        return <FontAwesomeIcon icon={faArrowUpRightDots} size="lg" />;

      // Time & Planning
      case 'calendar':
        return <FontAwesomeIcon icon={faCalendar} size="lg" />;
      case 'clock':
        return <FontAwesomeIcon icon={faClock} size="lg" />;
      case 'hourglass':
        return <FontAwesomeIcon icon={faHourglass} size="lg" />;
      case 'stopwatch':
        return <FontAwesomeIcon icon={faStopwatch} size="lg" />;

      // Locations & Travel
      case 'globe':
        return <FontAwesomeIcon icon={faGlobe} size="lg" />;
      case 'earth':
        return <FontAwesomeIcon icon={faEarthAmericas} size="lg" />;
      case 'map':
        return <FontAwesomeIcon icon={faMap} size="lg" />;
      case 'location':
        return <FontAwesomeIcon icon={faMapLocation} size="lg" />;
      case 'compass':
        return <FontAwesomeIcon icon={faCompass} size="lg" />;
      case 'anchor':
        return <FontAwesomeIcon icon={faAnchor} size="lg" />;

      // Business & Work
      case 'briefcase':
        return <FontAwesomeIcon icon={faBriefcase} size="lg" />;
      case 'building':
        return <FontAwesomeIcon icon={faBuilding} size="lg" />;
      case 'cogs':
        return <FontAwesomeIcon icon={faCogs} size="lg" />;
      case 'gears':
        return <FontAwesomeIcon icon={faGears} size="lg" />;
      case 'toolbox':
        return <FontAwesomeIcon icon={faToolbox} size="lg" />;
      case 'address-card':
        return <FontAwesomeIcon icon={faAddressCard} size="lg" />;
      case 'user':
        return <FontAwesomeIcon icon={faUser} size="lg" />;
      case 'user-gear':
        return <FontAwesomeIcon icon={faUserGear} size="lg" />;
      case 'people':
        return <FontAwesomeIcon icon={faPeopleGroup} size="lg" />;
      case 'print':
        return <FontAwesomeIcon icon={faPrint} size="lg" />;
      case 'credit-card':
        return <FontAwesomeIcon icon={faCreditCard} size="lg" />;
      case 'coins':
        return <FontAwesomeIcon icon={faCoins} size="lg" />;
      case 'shopping':
        return <FontAwesomeIcon icon={faCartShopping} size="lg" />;
      case 'bag':
        return <FontAwesomeIcon icon={faShoppingBag} size="lg" />;

      // Creative & Content
      case 'lightbulb':
        return <FontAwesomeIcon icon={faLightbulb} size="lg" />;
      case 'brain':
        return <FontAwesomeIcon icon={faBrain} size="lg" />;
      case 'edit':
        return <FontAwesomeIcon icon={faPenToSquare} size="lg" />;
      case 'design':
        return <FontAwesomeIcon icon={faPenRuler} size="lg" />;
      case 'image':
        return <FontAwesomeIcon icon={faImage} size="lg" />;
      case 'camera':
        return <FontAwesomeIcon icon={faCamera} size="lg" />;
      case 'music':
        return <FontAwesomeIcon icon={faMusic} size="lg" />;
      case 'palette':
        return <FontAwesomeIcon icon={faPalette} size="lg" />;
      case 'code':
        return <FontAwesomeIcon icon={faCode} size="lg" />;
      case 'terminal':
        return <FontAwesomeIcon icon={faTerminal} size="lg" />;

      // Markers & Statuses
      case 'bookmark':
        return <FontAwesomeIcon icon={faBookmark} size="lg" />;
      case 'tag':
        return <FontAwesomeIcon icon={faTag} size="lg" />;
      case 'note':
        return <FontAwesomeIcon icon={faStickyNote} size="lg" />;
      case 'list':
        return <FontAwesomeIcon icon={faRectangleList} size="lg" />;
      case 'flag':
        return <FontAwesomeIcon icon={faFlag} size="lg" />;
      case 'star':
        return <FontAwesomeIcon icon={faStar} size="lg" />;
      case 'heart':
        return <FontAwesomeIcon icon={faHeart} size="lg" />;
      case 'thumbs-up':
        return <FontAwesomeIcon icon={faThumbsUp} size="lg" />;
      case 'circle':
        return <FontAwesomeIcon icon={faCircle} size="lg" />;
      case 'bell':
        return <FontAwesomeIcon icon={faBell} size="lg" />;
      case 'info':
        return <FontAwesomeIcon icon={faInfo} size="lg" />;

      // Technology
      case 'robot':
        return <FontAwesomeIcon icon={faRobot} size="lg" />;
      case 'rocket':
        return <FontAwesomeIcon icon={faRocket} size="lg" />;
      case 'bolt':
        return <FontAwesomeIcon icon={faBolt} size="lg" />;
      case 'fire':
        return <FontAwesomeIcon icon={faFire} size="lg" />;
      case 'wifi':
        return <FontAwesomeIcon icon={faWifi} size="lg" />;
      case 'hdd':
        return <FontAwesomeIcon icon={faHdd} size="lg" />;
      case 'microchip':
        return <FontAwesomeIcon icon={faMicrochip} size="lg" />;
      case 'keyboard':
        return <FontAwesomeIcon icon={faKeyboard} size="lg" />;
      case 'link':
        return <FontAwesomeIcon icon={faLink} size="lg" />;
      case 'house':
        return <FontAwesomeIcon icon={faHouse} size="lg" />;

      // Miscellaneous
      case 'gem':
        return <FontAwesomeIcon icon={faGem} size="lg" />;
      case 'trophy':
        return <FontAwesomeIcon icon={faTrophy} size="lg" />;
      case 'graduation':
        return <FontAwesomeIcon icon={faGraduationCap} size="lg" />;
      case 'coffee':
        return <FontAwesomeIcon icon={faCoffee} size="lg" />;
      case 'chess':
        return <FontAwesomeIcon icon={faChess} size="lg" />;
      case 'puzzle':
        return <FontAwesomeIcon icon={faPuzzlePiece} size="lg" />;
      case 'shield':
        return <FontAwesomeIcon icon={faShield} size="lg" />;
      case 'flask':
        return <FontAwesomeIcon icon={faFlask} size="lg" />;
      case 'scroll':
        return <FontAwesomeIcon icon={faScroll} size="lg" />;
      case 'smile':
        return <FontAwesomeIcon icon={faFaceSmile} size="lg" />;
      case 'sun':
        return <FontAwesomeIcon icon={faSun} size="lg" />;
      case 'moon':
        return <FontAwesomeIcon icon={faMoon} size="lg" />;
      case 'search':
        return <FontAwesomeIcon icon={faMagnifyingGlass} size="lg" />;
      case 'copyright':
        return <FontAwesomeIcon icon={faCopyright} size="lg" />;

      default:
        return <FontAwesomeIcon icon={faEnvelope} size="lg" />;
    }
  };

  // Get icon for prompt (modal display with larger size)
  const getPromptModalIcon = (iconType: string | undefined) => {
    // Default to 'email' if iconType is undefined or null
    const icon = iconType || 'email';

    switch (icon) {
      // Communication
      case 'email':
        return <FontAwesomeIcon icon={faEnvelope} size="sm" />;
      case 'message':
        return <FontAwesomeIcon icon={faComment} size="sm" />;
      case 'paper-plane':
        return <FontAwesomeIcon icon={faPaperPlane} size="sm" />;
      case 'phone':
        return <FontAwesomeIcon icon={faPhone} size="sm" />;
      case 'bullhorn':
        return <FontAwesomeIcon icon={faBullhorn} size="sm" />;
      case 'headset':
        return <FontAwesomeIcon icon={faHeadset} size="sm" />;
      case 'microphone':
        return <FontAwesomeIcon icon={faMicrophone} size="sm" />;
      case 'video':
        return <FontAwesomeIcon icon={faVideo} size="sm" />;

      // Documents
      case 'document':
        return <FontAwesomeIcon icon={faFile} size="sm" />;
      case 'file-pdf':
        return <FontAwesomeIcon icon={faFilePdf} size="sm" />;
      case 'file-word':
        return <FontAwesomeIcon icon={faFileWord} size="sm" />;
      case 'file-excel':
        return <FontAwesomeIcon icon={faFileExcel} size="sm" />;
      case 'file-powerpoint':
        return <FontAwesomeIcon icon={faFilePowerpoint} size="sm" />;
      case 'file-image':
        return <FontAwesomeIcon icon={faFileImage} size="sm" />;
      case 'file-audio':
        return <FontAwesomeIcon icon={faFileAudio} size="sm" />;
      case 'file-video':
        return <FontAwesomeIcon icon={faFileVideo} size="sm" />;
      case 'file-code':
        return <FontAwesomeIcon icon={faFileCode} size="sm" />;
      case 'file-zipper':
        return <FontAwesomeIcon icon={faFileZipper} size="sm" />;
      case 'newspaper':
        return <FontAwesomeIcon icon={faNewspaper} size="sm" />;
      case 'folder':
        return <FontAwesomeIcon icon={faFolderOpen} size="sm" />;
      case 'clipboard':
        return <FontAwesomeIcon icon={faPaste} size="sm" />;
      case 'clone':
        return <FontAwesomeIcon icon={faClone} size="sm" />;

      // Analytics & Data
      case 'chart':
        return <FontAwesomeIcon icon={faChartBar} size="sm" />;
      case 'chart-pie':
        return <FontAwesomeIcon icon={faChartPie} size="sm" />;
      case 'signal':
        return <FontAwesomeIcon icon={faSignal} size="sm" />;
      case 'gauge':
        return <FontAwesomeIcon icon={faGaugeHigh} size="sm" />;
      case 'database':
        return <FontAwesomeIcon icon={faDatabase} size="sm" />;
      case 'table':
        return <FontAwesomeIcon icon={faTableList} size="sm" />;
      case 'sitemap':
        return <FontAwesomeIcon icon={faSitemap} size="sm" />;
      case 'diagram':
        return <FontAwesomeIcon icon={faDiagramProject} size="sm" />;
      case 'growth':
        return <FontAwesomeIcon icon={faArrowUpRightDots} size="sm" />;

      // Time & Planning
      case 'calendar':
        return <FontAwesomeIcon icon={faCalendar} size="sm" />;
      case 'clock':
        return <FontAwesomeIcon icon={faClock} size="sm" />;
      case 'hourglass':
        return <FontAwesomeIcon icon={faHourglass} size="sm" />;
      case 'stopwatch':
        return <FontAwesomeIcon icon={faStopwatch} size="sm" />;

      // Locations & Travel
      case 'globe':
        return <FontAwesomeIcon icon={faGlobe} size="sm" />;
      case 'earth':
        return <FontAwesomeIcon icon={faEarthAmericas} size="sm" />;
      case 'map':
        return <FontAwesomeIcon icon={faMap} size="sm" />;
      case 'location':
        return <FontAwesomeIcon icon={faMapLocation} size="sm" />;
      case 'compass':
        return <FontAwesomeIcon icon={faCompass} size="sm" />;
      case 'anchor':
        return <FontAwesomeIcon icon={faAnchor} size="sm" />;

      // Business & Work
      case 'briefcase':
        return <FontAwesomeIcon icon={faBriefcase} size="sm" />;
      case 'building':
        return <FontAwesomeIcon icon={faBuilding} size="sm" />;
      case 'cogs':
        return <FontAwesomeIcon icon={faCogs} size="sm" />;
      case 'gears':
        return <FontAwesomeIcon icon={faGears} size="sm" />;
      case 'toolbox':
        return <FontAwesomeIcon icon={faToolbox} size="sm" />;
      case 'address-card':
        return <FontAwesomeIcon icon={faAddressCard} size="sm" />;
      case 'user':
        return <FontAwesomeIcon icon={faUser} size="sm" />;
      case 'user-gear':
        return <FontAwesomeIcon icon={faUserGear} size="sm" />;
      case 'people':
        return <FontAwesomeIcon icon={faPeopleGroup} size="sm" />;
      case 'print':
        return <FontAwesomeIcon icon={faPrint} size="sm" />;
      case 'credit-card':
        return <FontAwesomeIcon icon={faCreditCard} size="sm" />;
      case 'coins':
        return <FontAwesomeIcon icon={faCoins} size="sm" />;
      case 'shopping':
        return <FontAwesomeIcon icon={faCartShopping} size="sm" />;
      case 'bag':
        return <FontAwesomeIcon icon={faShoppingBag} size="sm" />;

      // Creative & Content
      case 'lightbulb':
        return <FontAwesomeIcon icon={faLightbulb} size="sm" />;
      case 'brain':
        return <FontAwesomeIcon icon={faBrain} size="sm" />;
      case 'edit':
        return <FontAwesomeIcon icon={faPenToSquare} size="sm" />;
      case 'design':
        return <FontAwesomeIcon icon={faPenRuler} size="sm" />;
      case 'image':
        return <FontAwesomeIcon icon={faImage} size="sm" />;
      case 'camera':
        return <FontAwesomeIcon icon={faCamera} size="sm" />;
      case 'music':
        return <FontAwesomeIcon icon={faMusic} size="sm" />;
      case 'palette':
        return <FontAwesomeIcon icon={faPalette} size="sm" />;
      case 'code':
        return <FontAwesomeIcon icon={faCode} size="sm" />;
      case 'terminal':
        return <FontAwesomeIcon icon={faTerminal} size="sm" />;

      // Markers & Statuses
      case 'bookmark':
        return <FontAwesomeIcon icon={faBookmark} size="sm" />;
      case 'tag':
        return <FontAwesomeIcon icon={faTag} size="sm" />;
      case 'note':
        return <FontAwesomeIcon icon={faStickyNote} size="sm" />;
      case 'list':
        return <FontAwesomeIcon icon={faRectangleList} size="sm" />;
      case 'flag':
        return <FontAwesomeIcon icon={faFlag} size="sm" />;
      case 'star':
        return <FontAwesomeIcon icon={faStar} size="sm" />;
      case 'heart':
        return <FontAwesomeIcon icon={faHeart} size="sm" />;
      case 'thumbs-up':
        return <FontAwesomeIcon icon={faThumbsUp} size="sm" />;
      case 'circle':
        return <FontAwesomeIcon icon={faCircle} size="sm" />;
      case 'bell':
        return <FontAwesomeIcon icon={faBell} size="sm" />;
      case 'info':
        return <FontAwesomeIcon icon={faInfo} size="sm" />;

      // Technology
      case 'robot':
        return <FontAwesomeIcon icon={faRobot} size="sm" />;
      case 'rocket':
        return <FontAwesomeIcon icon={faRocket} size="sm" />;
      case 'bolt':
        return <FontAwesomeIcon icon={faBolt} size="sm" />;
      case 'fire':
        return <FontAwesomeIcon icon={faFire} size="sm" />;
      case 'wifi':
        return <FontAwesomeIcon icon={faWifi} size="sm" />;
      case 'hdd':
        return <FontAwesomeIcon icon={faHdd} size="sm" />;
      case 'microchip':
        return <FontAwesomeIcon icon={faMicrochip} size="sm" />;
      case 'keyboard':
        return <FontAwesomeIcon icon={faKeyboard} size="sm" />;
      case 'link':
        return <FontAwesomeIcon icon={faLink} size="sm" />;
      case 'house':
        return <FontAwesomeIcon icon={faHouse} size="sm" />;

      // Miscellaneous
      case 'gem':
        return <FontAwesomeIcon icon={faGem} size="sm" />;
      case 'trophy':
        return <FontAwesomeIcon icon={faTrophy} size="sm" />;
      case 'graduation':
        return <FontAwesomeIcon icon={faGraduationCap} size="sm" />;
      case 'coffee':
        return <FontAwesomeIcon icon={faCoffee} size="sm" />;
      case 'chess':
        return <FontAwesomeIcon icon={faChess} size="sm" />;
      case 'puzzle':
        return <FontAwesomeIcon icon={faPuzzlePiece} size="sm" />;
      case 'shield':
        return <FontAwesomeIcon icon={faShield} size="sm" />;
      case 'flask':
        return <FontAwesomeIcon icon={faFlask} size="sm" />;
      case 'scroll':
        return <FontAwesomeIcon icon={faScroll} size="sm" />;
      case 'smile':
        return <FontAwesomeIcon icon={faFaceSmile} size="sm" />;
      case 'sun':
        return <FontAwesomeIcon icon={faSun} size="sm" />;
      case 'moon':
        return <FontAwesomeIcon icon={faMoon} size="sm" />;
      case 'search':
        return <FontAwesomeIcon icon={faMagnifyingGlass} size="sm" />;
      case 'copyright':
        return <FontAwesomeIcon icon={faCopyright} size="sm" />;

      default:
        return <FontAwesomeIcon icon={faEnvelope} size="sm" />;
    }
  };

  // Process subprompt
  const processSubPrompt = async (subprompt: SubPrompt) => {
    if (!selectedTranscription) return;

    setProcessingSubprompt(true);
    setProcessedPromptText(null); // Clear previous text while loading

    try {
      const token = await getToken();
      const userId = localStorage.getItem('clerk-user-id');

      if (!token || !userId) {
        throw new Error('Authentication required');
      }

      // Replace placeholders in the prompt content
      const processedContent = subprompt.promptContent.replace(/{{transcription}}/g, selectedTranscription.textContent);

      const response = await fetch(`${API_URL}/prompts/process`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-clerk-user-id': userId
        },
        body: JSON.stringify({
          promptContent: processedContent,
          promptId: subprompt.globalPromptId,
          subPromptId: subprompt.id
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to process subprompt: ${response.statusText}`);
      }

      const data = await response.json();
      setProcessedPromptText(data.result);
      setSelectedSubprompt(subprompt);
    } catch (error) {
      console.error('Error processing subprompt:', error);
      showErrorToast(`Failed to process subprompt: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setProcessingSubprompt(false);
    }
  };

  // Function to handle opening the modal
  const handleOpenModal = (transcription: Transcription) => {
    setSelectedTranscription(transcription);
    setEditedSalesforceText(transcription.textContent);
    setActiveTab(0); // Set default tab to 0 instead of -1
    setOpenModal(true);
  };

  // Effect to process the prompt text when a tab changes or a transcription is selected
  useEffect(() => {
    // Only run for prompt tabs
    if (activeTab > 0 &&
      activeTab <= globalPrompts.filter(prompt => prompt.enabled).length &&
      selectedTranscription) {

      setLoadingPromptData(true);
      setPromptDisplayText(null);

      // Function to process the prompt
      const processPrompt = async () => {
        try {
          const token = await getToken();
          const userId = localStorage.getItem('clerk-user-id');

          if (!token || !userId) {
            throw new Error('Authentication required');
          }

          const currentPrompt = globalPrompts.filter(prompt => prompt.enabled)[activeTab - 1];

          // Replace placeholders in the prompt content
          const processedContent = currentPrompt.promptContent.replace(
            /{{transcription}}/g,
            selectedTranscription.textContent
          );

          const response = await fetch(`${API_URL}/prompts/process`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'x-clerk-user-id': userId
            },
            body: JSON.stringify({
              promptContent: processedContent,
              promptId: currentPrompt.id
            })
          });

          if (!response.ok) {
            throw new Error(`Failed to process prompt: ${response.statusText}`);
          }

          const data = await response.json();
          setPromptDisplayText(data.result);
        } catch (error) {
          console.error('Error processing prompt:', error);
          setPromptDisplayText("Error processing prompt. Please try again.");
          showErrorToast(`Failed to process prompt: ${error instanceof Error ? error.message : 'Unknown error'}`);
        } finally {
          setLoadingPromptData(false);
        }
      };

      // Process the prompt
      processPrompt();
    }
  }, [activeTab, selectedTranscription, getToken]);

  // When viewing a transcription, initialize the editing field
  useEffect(() => {
    if (selectedTranscription) {
      setEditedTranscription(selectedTranscription.textContent);
      setIsEditingTranscription(false);
    }
  }, [selectedTranscription]);

  // Handle saving the edited transcription
  const handleSaveTranscription = async () => {
    if (!selectedTranscription || !editedTranscription) {
      showWarningToast("No content to save");
      return;
    }

    try {
      setUpdatingTranscription(true);

      const token = await getToken();
      const userId = localStorage.getItem('clerk-user-id');

      if (!token || !userId) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`${API_URL}/transcribe/transcriptions/${selectedTranscription.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-clerk-user-id': userId
        },
        body: JSON.stringify({
          transcription: editedTranscription
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update transcription');
      }

      const updatedTranscription = await response.json();

      // Update the transcription in the state
      setSelectedTranscription({
        ...selectedTranscription,
        textContent: updatedTranscription.textContent
      });

      // Update the transcription in the list
      setTranscriptions(prevTranscriptions =>
        prevTranscriptions.map(t =>
          t.id === selectedTranscription.id
            ? { ...t, textContent: updatedTranscription.textContent }
            : t
        )
      );

      setIsEditingTranscription(false);
      showSuccessToast("Transcription updated successfully!");

    } catch (error: any) {
      console.error('Error updating transcription:', error);
      showErrorToast(error.message || 'Failed to update transcription');
    } finally {
      setUpdatingTranscription(false);
    }
  };

  // Helper function to get all tabs data
  const getAllTabs = () => {
    const tabs = [];

    // Transcription tab
    tabs.push({
      index: 0,
      title: "Transcription",
      icon: <FontAwesomeIcon icon={faStickyNote} style={{ fontSize: isMobileView ? '20px' : '28px' }} />,
      type: 'transcription'
    });

    // Dynamic prompt tabs
    globalPrompts.filter(prompt => prompt.enabled).forEach((prompt, index) => {
      tabs.push({
        index: index + 1,
        title: prompt.title,
        icon: <Box sx={{ fontSize: isMobileView ? '20px' : '28px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {getPromptIcon(prompt.iconType)}
        </Box>,
        type: 'prompt',
        promptData: prompt
      });
    });

    // Only add Slack and Salesforce tabs if not on carehub domain
    if (!isCareHubDomain) {
      // Slack tab
      const slackIndex = globalPrompts.filter(prompt => prompt.enabled).length + 1;
      tabs.push({
        index: slackIndex,
        title: "Share on Slack",
        icon: <SlackIcon size={isMobileView ? 20 : 40} />,
        type: 'slack'
      });

      // Salesforce tab
      const salesforceIndex = globalPrompts.filter(prompt => prompt.enabled).length + 2;
      tabs.push({
        index: salesforceIndex,
        title: "Send to Salesforce",
        icon: <SalesforceIcon size={isMobileView ? 20 : 40} />,
        type: 'salesforce'
      });
    }

    return tabs;
  };

  // Handle "More" menu open
  const handleMoreMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMoreMenuAnchorEl(event.currentTarget);
  };

  // Handle "More" menu close
  const handleMoreMenuClose = () => {
    setMoreMenuAnchorEl(null);
  };

  // Handle selecting a tab from the "More" menu
  const handleMoreTabSelect = (tabIndex: number) => {
    // Check if user is trying to access Slack or Salesforce tab when disabled
    const slackTabIndex = globalPrompts.filter(prompt => prompt.enabled).length + 1;
    const salesforceTabIndex = globalPrompts.filter(prompt => prompt.enabled).length + 2;

    if (!isCareHubDomain) {
      if (tabIndex === slackTabIndex && !isSlackEnabled) {
        setBlockedFeature('Slack Integration');
        setUpgradeModalOpen(true);
        setMoreMenuAnchorEl(null);
        return; // Don't change tab
      }

      if (tabIndex === salesforceTabIndex && !isSalesforceEnabled) {
        setBlockedFeature('Salesforce Integration');
        setUpgradeModalOpen(true);
        setMoreMenuAnchorEl(null);
        return; // Don't change tab
      }
    }

    setActiveTab(tabIndex);
    setMoreMenuAnchorEl(null);
    // Reset subprompt selection when changing tabs
    setSelectedSubprompt(null);
    setProcessedPromptText(null);
  };

  // Handle editing prompt results
  const handleEditPromptResult = (promptIndex: number, currentText: string) => {
    setEditingPromptIndex(promptIndex);
    setEditedPromptTexts(prev => ({
      ...prev,
      [promptIndex]: currentText
    }));
  };

  // Handle saving prompt edit
  const handleSavePromptEdit = (promptIndex: number) => {
    setEditingPromptIndex(null);
    // The edited text remains in state for download purposes
  };

  // Handle canceling prompt edit
  const handleCancelPromptEdit = () => {
    setEditingPromptIndex(null);
  };

  // Get the display text for a prompt (edited or original)
  const getPromptDisplayText = (promptIndex: number, originalText: string) => {
    return editedPromptTexts[promptIndex] || originalText;
  };

  // Handle opening contact form
  const handleOpenContactForm = (featureName: string) => {
    setContactFormData({
      name: user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : '',
      email: user?.email || '',
      message: '',
      feature: featureName
    });
    setUpgradeModalOpen(false);
    setContactFormOpen(true);
  };

  // Handle contact form field changes
  const handleContactFormChange = (field: string, value: string) => {
    setContactFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle contact form submission
  const handleContactFormSubmit = async () => {
    if (!contactFormData.name || !contactFormData.email) {
      showWarningToast("Please fill in your name and email");
      return;
    }

    try {
      setSendingContactForm(true);

      const token = await getToken();
      const userId = localStorage.getItem('clerk-user-id');

      // Include company name from settings
      const formDataWithCompany = {
        ...contactFormData,
        company: settings.companyName || 'Tranzcribe'
      };

      const response = await fetch(`${API_URL}/contact/professional-license`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
          ...(userId && { 'x-clerk-user-id': userId })
        },
        body: JSON.stringify(formDataWithCompany)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to send inquiry');
      }

      showSuccessToast(data.message || "Your inquiry has been sent successfully!");
      setContactFormOpen(false);
      setContactFormData({
        name: '',
        email: '',
        message: '',
        feature: ''
      });
    } catch (error) {
      console.error('Error sending contact form:', error);
      showErrorToast(`Failed to send inquiry: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSendingContactForm(false);
    }
  };

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh'
    }}>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, flexGrow: 1 }}>
        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <HistoryIcon sx={{ mr: 1, color: settings.primaryColor || '#5D5FEF', fontSize: 22 }} />
            <Typography variant="h6" fontWeight="bold">Transcription History</Typography>
          </Box>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by title or content..."
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: settings.primaryColor || '#5D5FEF' }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setSearchQuery('')}
                      edge="end"
                      size="small"
                      aria-label="clear search"
                    >
                      <CloseIcon />
                    </IconButton>
                  </InputAdornment>
                ) : null,
                sx: { borderRadius: 2 }
              }}
              // Add keyboard support for searching
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  // The search will be triggered by the debounce effect
                }
              }}
            />
            {loading && debouncedSearch && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <CircularProgress size={16} sx={{ mr: 1, color: settings.primaryColor || '#5D5FEF' }} />
                <Typography variant="caption" color="text.secondary">
                  Searching...
                </Typography>
              </Box>
            )}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
              <CircularProgress sx={{ color: settings.primaryColor || '#5D5FEF' }} />
            </Box>
          ) : transcriptions.length === 0 ? (
            <Alert severity="info">
              No transcriptions found. Start recording to see your history.
            </Alert>
          ) : (
            <>
              {/* Responsive: Cards on mobile, table on md+ */}
              <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                <Grid container spacing={2}>
                  {transcriptions.map((transcription) => (
                    <Grid item xs={12} key={transcription.id}>
                      <Card sx={{ p: 2, borderRadius: 2, boxShadow: 2 }}>
                        <CardContent sx={{ pb: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, wordBreak: 'break-word', whiteSpace: 'normal', overflow: 'visible', textOverflow: 'unset' }}>
                            {transcription.title}
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              {formatDistanceToNow(new Date(transcription.createdAt), { addSuffix: true })}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {formatDuration(transcription.duration)}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                            <Tooltip title="View Transcription">
                              <IconButton onClick={() => handleViewTranscription(transcription)} sx={{ color: settings.primaryColor || '#5D5FEF' }}>
                                <ViewIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download">
                              <IconButton onClick={() => handleDownload(transcription.textContent, `${transcription.title}.pdf`)} sx={{ color: settings.primaryColor || '#5D5FEF' }}>
                                <DownloadIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Use with Prompts">
                              <IconButton onClick={(e) => handlePromptMenuOpen(e, transcription.id)} sx={{ color: settings.primaryColor || '#5D5FEF' }}>
                                <MoreVertIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton onClick={() => openDeleteDialog(transcription.id)} sx={{ color: '#d32f2f' }}>
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
              {/* Table for md+ screens */}
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <TableContainer component={Paper} sx={{ boxShadow: 'none', mb: 3 }}>
                  <Table>
                    <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Title</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Duration</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transcriptions.map((transcription) => (
                        <TableRow key={transcription.id} hover>
                          <TableCell sx={{ maxWidth: 400, wordBreak: 'break-word', whiteSpace: 'normal', overflow: 'visible', textOverflow: 'unset' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{transcription.title}</Typography>
                          </TableCell>
                          <TableCell>
                            {formatDistanceToNow(new Date(transcription.createdAt), { addSuffix: true })}
                          </TableCell>
                          <TableCell>{formatDuration(transcription.duration)}</TableCell>
                          <TableCell>
                            <Tooltip title="View Transcription">
                              <IconButton
                                onClick={() => handleViewTranscription(transcription)}
                                sx={{ color: settings.primaryColor || '#5D5FEF' }}
                              >
                                <ViewIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download">
                              <IconButton
                                onClick={() => handleDownload(transcription.textContent, `${transcription.title}.pdf`)}
                                sx={{ color: settings.primaryColor || '#5D5FEF' }}
                              >
                                <DownloadIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Use with Prompts">
                              <IconButton
                                onClick={(e) => handlePromptMenuOpen(e, transcription.id)}
                                sx={{ color: settings.primaryColor || '#5D5FEF' }}
                              >
                                <MoreVertIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                onClick={() => openDeleteDialog(transcription.id)}
                                sx={{ color: '#d32f2f' }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={pagination.totalPages}
                  page={pagination.page}
                  onChange={handlePageChange}
                  color="primary"
                  sx={{
                    '& .MuiPaginationItem-root.Mui-selected': {
                      backgroundColor: settings.primaryColor || '#5D5FEF',
                      '&:hover': {
                        backgroundColor: settings.secondaryColor || '#4C4DCD',
                      }
                    }
                  }}
                />
              </Box>
            </>
          )}
        </Paper>

        {/* Prompt Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handlePromptMenuClose}
        >
          {globalPrompts.filter(prompt => prompt.enabled).map((prompt, index) => (
            <MenuItem key={prompt.id} onClick={() => handlePromptSelect(index)}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {getPromptIcon(prompt.iconType)}
                <Typography sx={{ ml: 1 }}>{prompt.title}</Typography>
              </Box>
            </MenuItem>
          ))}

          {/* Only show Slack and Salesforce options if not on carehub domain */}
          {!isCareHubDomain && (
            <>
              <Divider sx={{ my: 1 }} />

              {/* Slack sharing option */}
              <MenuItem onClick={() => handleSlackShare(activeTranscriptionId!)}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <SlackIcon size={20} />
                  <Typography sx={{ ml: 1 }}>Share on Slack</Typography>
                </Box>
              </MenuItem>

              {/* Salesforce sharing option */}
              <MenuItem onClick={() => handleSalesforceShare(activeTranscriptionId!)}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <SalesforceIcon size={20} />
                  <Typography sx={{ ml: 1 }}>Send to Salesforce</Typography>
                </Box>
              </MenuItem>
            </>
          )}
        </Menu>

        {/* Transcription Details Modal */}
        <Modal
          open={openModal && !!selectedTranscription}
          onClose={handleCloseModal}
          aria-labelledby="transcription-details-modal"
          aria-describedby="transcription-details"
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '95%', sm: '90%', md: '80%', lg: '75%' },
              maxWidth: '1200px',
              height: { xs: '85vh', sm: '80vh' },
              display: 'flex',
              flexDirection: 'column',
              bgcolor: 'background.paper',
              borderRadius: '8px',
              boxShadow: 24,
              p: { xs: 2, sm: 3, md: 4 },
            }}
          >
            {selectedTranscription && (
              <>
                {/* Header */}
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: { xs: 1, sm: 2 },
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { xs: 1, sm: 0 },
                  flexShrink: 0  // Prevent header from shrinking
                }}>
                  <Typography variant="h5" component="h2" sx={{
                    fontSize: { xs: '1.3rem', sm: '1.5rem', md: '1.75rem' },
                    fontWeight: 600,
                    color: '#333'
                  }}>
                    {selectedTranscription.title}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      size={isMobileView ? "small" : "medium"}
                      label={`Duration: ${formatDuration(selectedTranscription.duration)}`}
                      icon={<AccessTimeIcon sx={{ fontSize: isMobileView ? '0.9rem' : '1.1rem' }} />}
                      sx={{
                        backgroundColor: settings.primaryColor ? `${settings.primaryColor}20` : '#5D5FEF20',
                        color: settings.primaryColor || '#5D5FEF',
                        '& .MuiChip-icon': {
                          color: settings.primaryColor || '#5D5FEF'
                        }
                      }}
                    />

                    <Chip
                      size={isMobileView ? "small" : "medium"}
                      label={formatDistanceToNow(new Date(selectedTranscription.createdAt), { addSuffix: true })}
                      icon={<CalendarTodayIcon sx={{ fontSize: isMobileView ? '0.9rem' : '1.1rem' }} />}
                      sx={{
                        backgroundColor: settings.primaryColor ? `${settings.primaryColor}20` : '#5D5FEF20',
                        color: settings.primaryColor || '#5D5FEF',
                        '& .MuiChip-icon': {
                          color: settings.primaryColor || '#5D5FEF'
                        }
                      }}
                    />

                    <IconButton
                      onClick={handleCloseModal}
                      size="small"
                      sx={{
                        color: '#666',
                        '&:hover': {
                          color: '#333'
                        },
                        p: { xs: 0.5, sm: 1 }
                      }}
                    >
                      <CloseIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                    </IconButton>
                  </Box>
                </Box>

                <Divider sx={{ mb: { xs: 1.5, sm: 2 }, flexShrink: 0 }} />

                {/* Tab Navigation */}
                <Box sx={{
                  mb: 2,
                  mt: 1,
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0
                }}>
                  {(() => {
                    const allTabs = getAllTabs();
                    // On mobile, show only 1 tab directly and put rest in "More" menu
                    // On desktop, show 4 tabs directly  
                    const maxVisibleTabs = isMobileView ? 1 : 5;
                    const visibleTabs = allTabs.slice(0, maxVisibleTabs);
                    const hiddenTabs = allTabs.slice(maxVisibleTabs);

                    return (
                      <>
                        <Tabs
                          value={activeTab < maxVisibleTabs ? activeTab : false}
                          onChange={handleTabChange}
                          variant="standard"
                          textColor="primary"
                          indicatorColor="primary"
                          sx={{
                            '& .MuiTabs-indicator': {
                              backgroundColor: settings.primaryColor || '#5D5FEF',
                            },
                            '& .Mui-selected': {
                              color: settings.primaryColor || '#5D5FEF',
                            },
                            '& .MuiTabs-flexContainer': {
                              gap: 0
                            }
                          }}
                        >
                          {visibleTabs.map((tab) => (
                            <Tab
                              key={tab.index}
                              label={
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                  {tab.icon}
                                  {!isMobileView && tab.title}
                                </Box>
                              }
                              sx={{
                                textTransform: 'none',
                                width: isMobileView ? '80px' : '200px',
                                minWidth: isMobileView ? '80px' : '200px',
                                maxWidth: isMobileView ? '80px' : '200px'
                              }}
                              aria-label={tab.title}
                            />
                          ))}
                        </Tabs>

                        {hiddenTabs.length > 0 && (
                          <Box
                            onClick={handleMoreMenuOpen}
                            sx={{
                              width: isMobileView ? '80px' : '100px',
                              minWidth: isMobileView ? '80px' : '100px',
                              maxWidth: isMobileView ? '80px' : '100px',
                              height: '48px',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 0.5,
                              cursor: 'pointer',
                              textTransform: 'none',
                              borderBottom: activeTab >= maxVisibleTabs ? `2px solid ${settings.primaryColor || '#5D5FEF'}` : '2px solid transparent',
                              color: activeTab >= maxVisibleTabs ? (settings.primaryColor || '#5D5FEF') : '#666',
                              transition: 'all 0.2s ease-in-out',
                              '&:hover': {
                                color: settings.primaryColor || '#5D5FEF',
                                backgroundColor: 'rgba(93, 95, 239, 0.04)',
                              },
                              fontSize: '0.875rem',
                              fontWeight: activeTab >= maxVisibleTabs ? 500 : 400,
                              position: 'relative'
                            }}
                          >
                            <MoreVertIcon sx={{ fontSize: isMobileView ? '20px' : '28px' }} />
                            {isMobileView ? "Options" : "More"}
                          </Box>
                        )}
                      </>
                    );
                  })()}
                </Box>

                {/* More Menu */}
                <Menu
                  anchorEl={moreMenuAnchorEl}
                  open={Boolean(moreMenuAnchorEl)}
                  onClose={handleMoreMenuClose}
                  PaperProps={{
                    sx: {
                      maxHeight: 400,
                      width: isMobileView ? 280 : 350,
                      minWidth: isMobileView ? 280 : 350
                    }
                  }}
                >
                  {(() => {
                    const allTabs = getAllTabs();
                    const maxVisibleTabs = isMobileView ? 1 : 5;
                    const hiddenTabs = allTabs.slice(maxVisibleTabs);

                    return hiddenTabs.map((tab) => (
                      <MenuItem
                        key={tab.index}
                        onClick={() => handleMoreTabSelect(tab.index)}
                        selected={activeTab === tab.index}
                        sx={{
                          minHeight: '48px',
                          py: 1.5,
                          px: 2
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                          <Box sx={{ flexShrink: 0 }}>
                            {tab.icon}
                          </Box>
                          <Typography sx={{
                            flexGrow: 1,
                            wordBreak: 'break-word',
                            whiteSpace: 'normal',
                            lineHeight: 1.3,
                            fontSize: '0.875rem'
                          }}>
                            {tab.title}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ));
                  })()}
                </Menu>

                <Divider sx={{ mb: { xs: 1.5, sm: 2 }, flexShrink: 0 }} />

                {/* Content Area - Scrollable */}
                <Box sx={{
                  overflow: 'auto',
                  flexGrow: 1,
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  {/* Transcription Content */}
                  {activeTab === 0 && selectedTranscription && (
                    <Box
                      sx={{
                        width: { xs: '100%', sm: '90%', md: '85%' },
                        mx: 'auto',
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mb: { xs: 1, sm: 2 },
                          flexDirection: { xs: 'column', sm: 'row' },
                          gap: { xs: 1, sm: 0 }
                        }}
                      >
                        <Typography variant="subtitle2" color="#333" sx={{ fontSize: { xs: '1rem', sm: '1.1rem', md: '1.15rem' } }}>
                          Original Transcription
                        </Typography>

                        {/* Icons for copy/download/edit on mobile, buttons on desktop */}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {isMobileView ? (
                            <>
                              {!isEditingTranscription ? (
                                <>
                                  <IconButton
                                    size="small"
                                    sx={{ color: settings.primaryColor || "#333" }}
                                    onClick={() => {
                                      if (selectedTranscription) {
                                        navigator.clipboard.writeText(selectedTranscription.textContent);
                                        showSuccessToast("Copied to clipboard!");
                                      }
                                    }}
                                  >
                                    <ContentCopyIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    sx={{ color: settings.primaryColor || "#333" }}
                                    onClick={() =>
                                      handleDownload(
                                        selectedTranscription.textContent,
                                        `transcribe_${selectedTranscription.title.toLowerCase().replace(/\s+/g, "-")}.pdf`
                                      )
                                    }
                                  >
                                    <DownloadIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    sx={{ color: settings.primaryColor || "#333" }}
                                    onClick={() => setIsEditingTranscription(true)}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </>
                              ) : (
                                <IconButton
                                  size="small"
                                  sx={{ color: settings.primaryColor || "#333" }}
                                  onClick={handleSaveTranscription}
                                  disabled={updatingTranscription}
                                >
                                  {updatingTranscription ? <CircularProgress size={18} /> : <SaveIcon fontSize="small" />}
                                </IconButton>
                              )}
                            </>
                          ) : (
                            <>
                              {!isEditingTranscription ? (
                                <>
                                  <Button
                                    sx={{
                                      textTransform: "none",
                                      color: settings.primaryColor || "#333",
                                      "&:hover": {
                                        backgroundColor: "rgba(0,0,0,0.05)",
                                      },
                                      padding: "4px 8px",
                                      minWidth: "unset",
                                    }}
                                    startIcon={<ContentCopyIcon fontSize="small" />}
                                    onClick={() => {
                                      if (selectedTranscription) {
                                        navigator.clipboard.writeText(selectedTranscription.textContent);
                                        showSuccessToast("Copied to clipboard!");
                                      }
                                    }}
                                  >
                                    Copy
                                  </Button>
                                  <Button
                                    color="inherit"
                                    sx={{
                                      textTransform: "none",
                                      color: settings.primaryColor || "#333",
                                      "&:hover": {
                                        backgroundColor: "rgba(0,0,0,0.05)",
                                      },
                                    }}
                                    onClick={() => {
                                      const currentPromptIndex = activeTab - 1;
                                      const textToDownload = getPromptDisplayText(currentPromptIndex, processedPromptText || promptDisplayText || selectedTranscription.textContent);
                                      const prompt = globalPrompts.filter(prompt => prompt.enabled)[activeTab - 1];
                                      handleDownload(
                                        textToDownload,
                                        `transcribe_${prompt.title.toLowerCase().replace(/\s+/g, "-")}.pdf`
                                      );
                                    }}
                                  >
                                    Download
                                  </Button>
                                  {editingPromptIndex === activeTab - 1 ? (
                                    <Button
                                      variant="outlined"
                                      startIcon={<SaveIcon />}
                                      sx={{
                                        borderColor: settings.primaryColor || '#5D5FEF',
                                        color: settings.primaryColor || '#5D5FEF',
                                        '&:hover': {
                                          borderColor: settings.secondaryColor || '#4C4DCD',
                                          backgroundColor: 'rgba(93, 95, 239, 0.04)',
                                        },
                                      }}
                                      onClick={() => handleSavePromptEdit(activeTab - 1)}
                                    >
                                      Save
                                    </Button>
                                  ) : (
                                    <Button
                                      variant="outlined"
                                      startIcon={<EditIcon />}
                                      sx={{
                                        borderColor: settings.primaryColor || '#5D5FEF',
                                        color: settings.primaryColor || '#5D5FEF',
                                        '&:hover': {
                                          borderColor: settings.secondaryColor || '#4C4DCD',
                                          backgroundColor: 'rgba(93, 95, 239, 0.04)',
                                        },
                                      }}
                                      onClick={() => handleEditPromptResult(activeTab - 1, processedPromptText || promptDisplayText || "")}
                                    >
                                      Edit
                                    </Button>
                                  )}
                                </>
                              ) : (
                                <Button
                                  variant="outlined"
                                  startIcon={updatingTranscription ? <CircularProgress size={18} /> : <SaveIcon />}
                                  sx={{
                                    borderColor: settings.primaryColor || '#5D5FEF',
                                    color: settings.primaryColor || '#5D5FEF',
                                    '&:hover': {
                                      borderColor: settings.secondaryColor || '#4C4DCD',
                                      backgroundColor: 'rgba(93, 95, 239, 0.04)',
                                    },
                                  }}
                                  onClick={handleSaveTranscription}
                                  disabled={updatingTranscription}
                                >
                                  {updatingTranscription ? "Saving..." : "Save"}
                                </Button>
                              )}
                            </>
                          )}
                        </Box>
                      </Box>

                      {isEditingTranscription ? (
                        <TextField
                          fullWidth
                          multiline
                          minRows={4}
                          maxRows={16}
                          variant="outlined"
                          value={editedTranscription}
                          onChange={(e) => setEditedTranscription(e.target.value)}
                          sx={{
                            mb: 2,
                            flexGrow: 1
                          }}
                        />
                      ) : (
                        <Paper
                          elevation={0}
                          sx={{
                            p: { xs: 1.5, sm: 2, md: 3 },
                            backgroundColor: '#f9f9f9',
                            borderRadius: '8px',
                            flexGrow: 1,
                            height: 'auto',
                            minHeight: '100px'
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              whiteSpace: 'pre-line',
                              fontSize: { xs: '0.875rem', sm: '0.95rem', md: '1rem' },
                              lineHeight: 1.6
                            }}
                          >
                            {selectedTranscription.textContent}
                          </Typography>
                        </Paper>
                      )}
                    </Box>
                  )}

                  {/* Prompts Tabs */}
                  {activeTab > 0 &&
                    activeTab <= globalPrompts.filter(prompt => prompt.enabled).length &&
                    selectedTranscription && (
                      <Box sx={{
                        width: { xs: '100%', sm: '90%', md: '85%' },
                        mx: 'auto',
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column'
                      }}>
                        {/* Override the default PromptTab styling with our custom container */}
                        <Box sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'stretch',
                          backgroundColor: 'transparent',
                          flexGrow: 1,
                          p: 0
                        }}>
                          {/* Header with title and actions */}
                          <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: { xs: 1, sm: 2 },
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: { xs: 1, sm: 0 }
                          }}>
                            <Typography variant="subtitle2" color="#333" sx={{ fontSize: { xs: '1rem', sm: '1.1rem', md: '1.15rem' } }}>
                              {selectedSubprompt?.title || globalPrompts.filter(prompt => prompt.enabled)[activeTab - 1].title}
                            </Typography>

                            <Box sx={{ display: 'flex', gap: 1 }}>
                              {isMobileView ? (
                                <>
                                  <IconButton
                                    size="small"
                                    sx={{ color: settings.primaryColor || "#333" }}
                                    onClick={() => {
                                      const currentPromptIndex = activeTab - 1;
                                      const textToCopy = getPromptDisplayText(currentPromptIndex, processedPromptText || promptDisplayText || selectedTranscription.textContent);
                                      navigator.clipboard.writeText(textToCopy);
                                      showSuccessToast("Copied to clipboard!");
                                    }}
                                  >
                                    <ContentCopyIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    sx={{ color: settings.primaryColor || "#333" }}
                                    onClick={() => {
                                      const currentPromptIndex = activeTab - 1;
                                      const textToDownload = getPromptDisplayText(currentPromptIndex, processedPromptText || promptDisplayText || selectedTranscription.textContent);
                                      const prompt = globalPrompts.filter(prompt => prompt.enabled)[activeTab - 1];
                                      handleDownload(
                                        textToDownload,
                                        `transcribe_${prompt.title.toLowerCase().replace(/\s+/g, "-")}.pdf`
                                      );
                                    }}
                                  >
                                    <DownloadIcon fontSize="small" />
                                  </IconButton>
                                  {editingPromptIndex === activeTab - 1 ? (
                                    <IconButton
                                      size="small"
                                      sx={{ color: settings.primaryColor || "#333" }}
                                      onClick={() => handleSavePromptEdit(activeTab - 1)}
                                    >
                                      <SaveIcon fontSize="small" />
                                    </IconButton>
                                  ) : (
                                    <IconButton
                                      size="small"
                                      sx={{ color: settings.primaryColor || "#333" }}
                                      onClick={() => handleEditPromptResult(activeTab - 1, processedPromptText || promptDisplayText || "")}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  )}
                                </>
                              ) : (
                                <>
                                  <Button
                                    sx={{
                                      textTransform: "none",
                                      color: settings.primaryColor || "#333",
                                      "&:hover": {
                                        backgroundColor: "rgba(0,0,0,0.05)",
                                      },
                                      padding: "4px 8px",
                                      minWidth: "unset",
                                    }}
                                    startIcon={<ContentCopyIcon fontSize="small" />}
                                    onClick={() => {
                                      const currentPromptIndex = activeTab - 1;
                                      const textToCopy = getPromptDisplayText(currentPromptIndex, processedPromptText || promptDisplayText || selectedTranscription.textContent);
                                      navigator.clipboard.writeText(textToCopy);
                                      showSuccessToast("Copied to clipboard!");
                                    }}
                                  >
                                    Copy
                                  </Button>
                                  <Button
                                    color="inherit"
                                    sx={{
                                      textTransform: "none",
                                      color: settings.primaryColor || "#333",
                                      "&:hover": {
                                        backgroundColor: "rgba(0,0,0,0.05)",
                                      },
                                    }}
                                    onClick={() => {
                                      const currentPromptIndex = activeTab - 1;
                                      const textToDownload = getPromptDisplayText(currentPromptIndex, processedPromptText || promptDisplayText || selectedTranscription.textContent);
                                      const prompt = globalPrompts.filter(prompt => prompt.enabled)[activeTab - 1];
                                      handleDownload(
                                        textToDownload,
                                        `transcribe_${prompt.title.toLowerCase().replace(/\s+/g, "-")}.pdf`
                                      );
                                    }}
                                  >
                                    Download
                                  </Button>
                                  {editingPromptIndex === activeTab - 1 ? (
                                    <Button
                                      variant="outlined"
                                      startIcon={<SaveIcon />}
                                      sx={{
                                        borderColor: settings.primaryColor || '#5D5FEF',
                                        color: settings.primaryColor || '#5D5FEF',
                                        '&:hover': {
                                          borderColor: settings.secondaryColor || '#4C4DCD',
                                          backgroundColor: 'rgba(93, 95, 239, 0.04)',
                                        },
                                      }}
                                      onClick={() => handleSavePromptEdit(activeTab - 1)}
                                    >
                                      Save
                                    </Button>
                                  ) : (
                                    <Button
                                      variant="outlined"
                                      startIcon={<EditIcon />}
                                      sx={{
                                        borderColor: settings.primaryColor || '#5D5FEF',
                                        color: settings.primaryColor || '#5D5FEF',
                                        '&:hover': {
                                          borderColor: settings.secondaryColor || '#4C4DCD',
                                          backgroundColor: 'rgba(93, 95, 239, 0.04)',
                                        },
                                      }}
                                      onClick={() => handleEditPromptResult(activeTab - 1, processedPromptText || promptDisplayText || "")}
                                    >
                                      Edit
                                    </Button>
                                  )}
                                </>
                              )}
                            </Box>
                          </Box>

                          {/* Use a hidden PromptTab to process the content but don't display its UI */}
                          <Box sx={{ display: 'none' }}>
                            <PromptTab
                              prompt={globalPrompts.filter(prompt => prompt.enabled)[activeTab - 1]}
                              inputText={selectedTranscription.textContent}
                              handleDownload={handleDownload}
                              overrideText={processedPromptText}
                              overrideTitle={selectedSubprompt?.title}
                            />
                          </Box>

                          {/* Display content in our standardized format */}
                          {editingPromptIndex === activeTab - 1 ? (
                            <TextField
                              fullWidth
                              multiline
                              minRows={4}
                              maxRows={16}
                              variant="outlined"
                              value={editedPromptTexts[activeTab - 1] || ""}
                              onChange={(e) => setEditedPromptTexts(prev => ({
                                ...prev,
                                [activeTab - 1]: e.target.value
                              }))}
                              sx={{
                                mb: 2,
                                flexGrow: 1
                              }}
                            />
                          ) : (
                            <Paper
                              elevation={0}
                              sx={{
                                p: { xs: 1.5, sm: 2, md: 3 },
                                backgroundColor: '#f9f9f9',
                                borderRadius: '8px',
                                flexGrow: 1,
                                height: 'auto',
                                minHeight: '100px',
                                position: 'relative'
                              }}
                            >
                              {(loadingPromptData || processingSubprompt) && (
                                <Box sx={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: 'rgba(249,249,249,0.7)',
                                  zIndex: 1,
                                  borderRadius: '8px'
                                }}>
                                  <CircularProgress size={40} sx={{ color: settings.primaryColor || '#5D5FEF' }} />
                                </Box>
                              )}
                              <Typography
                                variant="body1"
                                sx={{
                                  whiteSpace: 'pre-line',
                                  fontSize: { xs: '0.875rem', sm: '0.95rem', md: '1rem' },
                                  lineHeight: 1.6
                                }}
                              >
                                {getPromptDisplayText(activeTab - 1, processedPromptText || promptDisplayText || "Processing your request...")}
                              </Typography>
                            </Paper>
                          )}
                        </Box>

                        {/* Display subprompts if available */}
                        {globalPrompts.filter(prompt => prompt.enabled)[activeTab - 1].subPrompts &&
                          globalPrompts.filter(prompt => prompt.enabled)[activeTab - 1].subPrompts!.length > 0 && (
                            <Box sx={{
                              width: '100%',
                              mt: 2,
                              mb: 2,
                              px: 1,
                              display: 'flex',
                              overflowX: 'auto',
                              gap: 1,
                              pb: 1,
                              '&::-webkit-scrollbar': {
                                height: '6px',
                              },
                              '&::-webkit-scrollbar-track': {
                                backgroundColor: 'rgba(0,0,0,0.1)',
                                borderRadius: '3px',
                              },
                              '&::-webkit-scrollbar-thumb': {
                                backgroundColor: settings.primaryColor || '#5D5FEF',
                                borderRadius: '3px',
                              },
                            }}>
                              {globalPrompts.filter(prompt => prompt.enabled)[activeTab - 1].subPrompts!
                                .filter(subprompt => subprompt.enabled)
                                .map((subprompt) => (
                                  <Button
                                    key={subprompt.id}
                                    variant={selectedSubprompt?.id === subprompt.id ? "contained" : "outlined"}
                                    size="small"
                                    disabled={processingSubprompt}
                                    startIcon={getPromptIcon(subprompt.iconType)}
                                    onClick={() => processSubPrompt(subprompt)}
                                    sx={{
                                      textTransform: 'none',
                                      borderRadius: '20px',
                                      px: 2,
                                      flexShrink: 0,
                                      ...(selectedSubprompt?.id === subprompt.id
                                        ? {
                                          backgroundColor: settings.primaryColor || '#5D5FEF',
                                          color: '#fff',
                                          '&:hover': {
                                            backgroundColor: settings.secondaryColor || '#4C4DCD',
                                          },
                                        }
                                        : {
                                          color: settings.primaryColor || '#5D5FEF',
                                          borderColor: settings.primaryColor || '#5D5FEF',
                                          '&:hover': {
                                            borderColor: settings.secondaryColor || '#4C4DCD',
                                            backgroundColor: 'rgba(93, 95, 239, 0.04)',
                                          },
                                        }),
                                    }}
                                  >
                                    {subprompt.title}
                                    {processingSubprompt && selectedSubprompt?.id === subprompt.id &&
                                      <CircularProgress size={16} sx={{ ml: 1, color: '#fff' }} />}
                                  </Button>
                                ))}
                            </Box>
                          )}
                      </Box>
                    )}

                  {/* Slack sharing tab content */}
                  {!isCareHubDomain && activeTab === globalPrompts.filter(prompt => prompt.enabled).length + 1 && selectedTranscription && (
                    <Box
                      sx={{
                        width: { xs: '100%', sm: '90%', md: '85%' },
                        mx: 'auto',
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        opacity: isSlackEnabled ? 1 : 0.5,
                        userSelect: isSlackEnabled ? 'auto' : 'none'
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mb: { xs: 1, sm: 2 },
                          flexDirection: { xs: 'column', sm: 'row' },
                          gap: { xs: 1, sm: 0 }
                        }}
                      >
                        <Typography variant="h6" color="#333" sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>Share on Slack</Typography>
                        <Button
                          onClick={handleShareToSlack}
                          variant="contained"
                          disabled={shareToSlackLoading}
                          startIcon={shareToSlackLoading ? <CircularProgress size={20} /> : <SlackIcon size={20} />}
                          sx={{
                            backgroundColor: settings.primaryColor || '#5D5FEF',
                            fontSize: { xs: '0.8rem', sm: '0.875rem' },
                            px: { xs: 2, sm: 3 },
                            '&:hover': {
                              backgroundColor: settings.secondaryColor || '#4C4DCD',
                            },
                            '&.Mui-disabled': {
                              backgroundColor: 'rgba(0, 0, 0, 0.12)',
                            },
                            opacity: isSlackEnabled ? 1 : 0.6,
                            cursor: isSlackEnabled ? 'pointer' : 'not-allowed'
                          }}
                        >
                          {shareToSlackLoading ?
                            (isMobileView ? "..." : "Sharing...") :
                            (isMobileView ? "Share" : "Share to Slack")}
                        </Button>
                      </Box>
                      <Typography sx={{
                        whiteSpace: "pre-line",
                        mb: 2,
                        fontSize: { xs: '0.9rem', sm: '1rem' }
                      }}>
                        Your transcription will be shared to a channel on Slack.
                      </Typography>
                      <Paper
                        elevation={0}
                        sx={{
                          p: { xs: 1.5, sm: 2, md: 3 },
                          backgroundColor: '#f9f9f9',
                          borderRadius: '8px',
                          flexGrow: 1,
                          height: 'auto',
                          minHeight: '100px'
                        }}
                      >
                        <Typography variant="body1" sx={{
                          whiteSpace: "pre-line",
                          fontSize: { xs: '0.875rem', sm: '0.95rem', md: '1rem' },
                          lineHeight: 1.6
                        }}>
                          {selectedTranscription.textContent}
                        </Typography>
                      </Paper>
                    </Box>
                  )}

                  {/* Salesforce sharing tab content */}
                  {!isCareHubDomain && activeTab === globalPrompts.filter(prompt => prompt.enabled).length + 2 && selectedTranscription && (
                    <Box
                      sx={{
                        width: { xs: '100%', sm: '90%', md: '85%' },
                        mx: 'auto',
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        opacity: isSalesforceEnabled ? 1 : 0.5,
                        userSelect: isSalesforceEnabled ? 'auto' : 'none'
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mb: { xs: 1, sm: 2 },
                          flexDirection: { xs: 'column', sm: 'row' },
                          gap: { xs: 1, sm: 0 }
                        }}
                      >
                        <Typography variant="h6" color="#333" sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>Send to Salesforce</Typography>
                        <Button
                          onClick={handleShareToSalesforce}
                          variant="contained"
                          disabled={shareToSalesforceLoading}
                          startIcon={shareToSalesforceLoading ? <CircularProgress size={20} /> : <SalesforceIcon size={20} />}
                          sx={{
                            backgroundColor: settings.primaryColor || '#5D5FEF',
                            fontSize: { xs: '0.8rem', sm: '0.875rem' },
                            '&:hover': {
                              backgroundColor: settings.secondaryColor || '#4C4DCD',
                            },
                            '&.Mui-disabled': {
                              backgroundColor: 'rgba(0, 0, 0, 0.12)',
                            },
                            opacity: isSalesforceEnabled ? 1 : 0.6,
                            cursor: isSalesforceEnabled ? 'pointer' : 'not-allowed'
                          }}
                        >
                          {shareToSalesforceLoading ?
                            (isMobileView ? "..." : "Sending...") :
                            (isMobileView ? "Send" : "Send to Salesforce")}
                        </Button>
                      </Box>
                      <Typography sx={{
                        whiteSpace: "pre-line",
                        mb: 2,
                        fontSize: { xs: '0.9rem', sm: '1rem' }
                      }}>
                        {salesforceError && (
                          <Alert
                            severity="error"
                            sx={{ mt: 2 }}
                            onClose={() => setSalesforceError(null)}
                          >
                            {salesforceError}
                          </Alert>
                        )}
                        {!salesforceError && (
                          <Typography>
                            Your transcription will be sent to Salesforce with your email: {user?.email || 'No email found'}
                          </Typography>
                        )}
                      </Typography>

                      <Paper
                        elevation={0}
                        sx={{
                          p: { xs: 1.5, sm: 2, md: 3 },
                          backgroundColor: '#f9f9f9',
                          borderRadius: '8px',
                          flexGrow: 1,
                          height: 'auto',
                          minHeight: '100px'
                        }}
                      >
                        <Typography variant="body1" sx={{
                          whiteSpace: "pre-line",
                          fontSize: { xs: '0.875rem', sm: '0.95rem', md: '1rem' },
                          lineHeight: 1.6
                        }}>
                          {selectedTranscription.textContent}
                        </Typography>
                      </Paper>
                    </Box>
                  )}
                </Box>
              </>
            )}
          </Box>
        </Modal>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle sx={{ pb: 2 }}>Confirm Deletion</DialogTitle>
          <DialogContent sx={{ pb: 3 }}>
            <Typography>
              Are you sure you want to delete this transcription? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              sx={{
                color: settings.primaryColor || '#5D5FEF',
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteTranscription}
              variant="contained"
              color="error"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Container>

      <Footer />

      {/* Upgrade Modal */}
      <Dialog
        open={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold', fontSize: '1.5rem' }}>
          Professional License Required
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Dear customer, this option is available in our professional license.{' '}
            <Typography
              component="span"
              onClick={() => handleOpenContactForm(blockedFeature || 'Slack Integration')}
              sx={{
                color: settings.primaryColor || '#5D5FEF',
                cursor: 'pointer',
                textDecoration: 'underline',
                fontWeight: 'medium',
                '&:hover': {
                  color: settings.secondaryColor || '#4C4DCD',
                }
              }}
            >
              Click here if you want more information.
            </Typography>
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setUpgradeModalOpen(false)}
            variant="contained"
            sx={{
              backgroundColor: settings.primaryColor || '#5D5FEF',
              '&:hover': {
                backgroundColor: settings.secondaryColor || '#4C4DCD',
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Contact Form Modal */}
      <Dialog
        open={contactFormOpen}
        onClose={() => !sendingContactForm && setContactFormOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold', fontSize: '1.5rem' }}>
          Request Professional License Information
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
            Please fill out the form below and we'll get back to you shortly.
          </Typography>

          <TextField
            fullWidth
            label="Name *"
            value={contactFormData.name}
            onChange={(e) => handleContactFormChange('name', e.target.value)}
            margin="normal"
            disabled={sendingContactForm}
          />

          <TextField
            fullWidth
            label="Email *"
            type="email"
            value={contactFormData.email}
            onChange={(e) => handleContactFormChange('email', e.target.value)}
            margin="normal"
            disabled={sendingContactForm}
          />

          <TextField
            fullWidth
            label="Message"
            multiline
            rows={4}
            value={contactFormData.message}
            onChange={(e) => handleContactFormChange('message', e.target.value)}
            margin="normal"
            placeholder="Tell us about your needs..."
            disabled={sendingContactForm}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            onClick={() => setContactFormOpen(false)}
            disabled={sendingContactForm}
          >
            Cancel
          </Button>
          <Button
            onClick={handleContactFormSubmit}
            variant="contained"
            disabled={sendingContactForm}
            startIcon={sendingContactForm ? <CircularProgress size={20} /> : null}
            sx={{
              backgroundColor: settings.primaryColor || '#5D5FEF',
              '&:hover': {
                backgroundColor: settings.secondaryColor || '#4C4DCD',
              }
            }}
          >
            {sendingContactForm ? 'Sending...' : 'Send Inquiry'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HistoryPage; 